"use client";

import { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { Calendar, Download } from "lucide-react";

const AllStudents = () => {
  const {
    loginUserEmail,
    setLoginUserEmail,
    studentInfo,
    setStudentInfo,
    setStudentDetails,
    setAcademicRecords,
    entryTestRecords,
    setEntryTestRecords,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    email: "",
    program: "All Programs",
    degree: "All Degrees",
    status: "All Status",
  });
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("All Programs");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [selectedDegree, setSelectedDegree] = useState("All Degrees");
  const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isDegreeDropdownOpen, setIsDegreeDropdownOpen] = useState(false);
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [selectedAction, setSelectedAction] = useState("All Actions");
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [loadingApprove, setLoadingApprove] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const filteredStudents = students.filter((student) => {
    const nameMatch = student.name
      .toLowerCase()
      .includes(appliedSearchTerm.toLowerCase());
    const programMatch =
      selectedProgram === "All Programs" || student.Program === selectedProgram;
    const statusMatch =
      selectedStatus === "All Status" ||
      (selectedStatus === "Paid" && student.status === "1") ||
      (selectedStatus === "Pending" && student.status === "0");
    const degreeMatch =
      selectedDegree === "All Degrees" ||
      (selectedDegree === "BS" && student.Program.startsWith("BS")) ||
      (selectedDegree === "MS" && student.Program.startsWith("MS"));
    const actionMatch =
      selectedAction === "All Actions" ||
      (selectedAction === "Approve" && student.admit_card == null) ||
      (selectedAction === "Approved" && student.admit_card === 1);

    return (
      nameMatch && programMatch && statusMatch && degreeMatch && actionMatch
    );
  });

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await api.get("/students/search-students", {
        params: {
          email: searchParams.email,
          program:
            searchParams.program !== "All Programs" ? searchParams.program : "",
          degree:
            searchParams.degree !== "All Degrees" ? searchParams.degree : "",
          status:
            searchParams.status !== "All Status"
              ? searchParams.status === "Paid"
                ? "paid"
                : "pending"
              : "",
          action: selectedAction !== "All Actions" ? selectedAction : "",
        },
      });

      setStudents(response.data);
    } catch (error) {
      console.error("Error searching students:", error);
      showToast("error", "Failed to search students");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = async (loginUserEmail) => {
    try {
      setViewModalLoading(loginUserEmail);

      // Get student info
      const studentResponse = await api.get(
        `/students/student-info?email=${loginUserEmail}&allInfo=1`
      );

      // Get academic records
      const academicResponse = await api.get(
        `/students/academic-records?email=${loginUserEmail}`
      );
      const entryTestResponse = await api.get(
        `/students/entry-test-data?email=${loginUserEmail}`
      );

      setStudentDetails(studentResponse.data);
      setAcademicRecords(academicResponse.data);
      setEntryTestRecords(entryTestResponse.data);

      // setShowViewModal(true);
      navigate("/ShowStudentForm");
    } catch (error) {
      console.error("Error fetching student details:", error);
      showToast("error", "Failed to fetch student details");
    } finally {
      setViewModalLoading(false);
    }
  };

  const handleEditClick = async (loginUserEmail) => {
    try {
      setViewModalLoading(loginUserEmail);

      // Get student info
      const studentResponse = await api.get(
        `/students/student-info?email=${loginUserEmail}&allInfo=1`
      );

      // Get academic records
      const academicResponse = await api.get(
        `/students/academic-records?email=${loginUserEmail}`
      );

      setStudentDetails(studentResponse.data);
      setAcademicRecords(academicResponse.data);

      // Navigate to the student form for editing
      navigate("/studentform", { state: { isEditing: true } });
    } catch (error) {
      console.error("Error fetching student details for edit:", error);
      showToast("error", "Failed to fetch student details for editing");
    } finally {
      setViewModalLoading(false);
    }
  };

  const handleDeleteClick = (email) => {
    setStudentToDelete(email);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!studentToDelete) return;

    setLoadingDelete(true);
    try {
      // Show loading toast
      showToast("info", "Deleting student record...");

      const response = await api.delete("/students/delete-student", {
        data: { email: studentToDelete },
      });

      if (response.status === 200) {
        showToast("success", "Student record deleted successfully");
        // Refresh the student list
        handleSearch();
      } else {
        showToast(
          "error",
          response.data?.message || "Failed to delete student"
        );
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      showToast(
        "error",
        error.response?.data?.message || "Failed to delete student"
      );
    } finally {
      setLoadingDelete(false);
      setShowDeleteModal(false);
      setStudentToDelete(null);
    }
  };

  const showToast = (type, message) => {
    const toast = document.createElement("div");
    const bgColorClass =
      type === "success"
        ? "text-green-500 bg-green-100 dark:bg-green-800 dark:text-green-200"
        : type === "error"
        ? "text-red-500 bg-red-100 dark:bg-red-800 dark:text-red-200"
        : "text-blue-500 bg-blue-100 dark:bg-blue-800 dark:text-blue-200";

    toast.className = `fixed top-4 right-4 flex items-center w-full max-w-xs p-4 mb-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800 ${bgColorClass}`;

    toast.innerHTML = `
      <div class="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 ${bgColorClass} rounded-lg">
        <svg class="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
          ${
            type === "success"
              ? `<path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />`
              : type === "error"
              ? `<path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z" />`
              : `<path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z" />`
          }
        </svg>
      </div>
      <div class="ml-3 text-sm font-normal">${message}</div>
    `;
    document.body.appendChild(toast);

    setTimeout(() => toast.remove(), 3000);
  };

  const handleApprove = async (studentEmail) => {
    try {
      // Set loading state for this specific student
      setLoadingApprove((prev) => ({ ...prev, [studentEmail]: true }));

      // Show loading toast
      showToast("info", "Approving student...");

      const response = await api.put("/students/approve-student", {
        email: studentEmail,
      });

      if (response.data && response.status === 200) {
        showToast("success", "Student approved successfully");
        // Refresh the student list to show updated status
        handleSearch();
      } else {
        showToast(
          "error",
          response.data?.message || "Failed to approve student"
        );
      }
    } catch (error) {
      console.error("Error approving student:", error);
      showToast(
        "error",
        error.response?.data?.message || "Failed to approve student"
      );
    } finally {
      // Clear loading state
      setLoadingApprove((prev) => ({ ...prev, [studentEmail]: false }));
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      showToast("info", "Preparing Excel file...");

      // Build query parameters
      const params = new URLSearchParams();
      if (searchParams.email) params.append("email", searchParams.email);
      if (searchParams.program !== "All Programs")
        params.append("program", searchParams.program);
      if (searchParams.degree !== "All Degrees")
        params.append("degree", searchParams.degree);
      if (searchParams.status !== "All Status") {
        params.append(
          "status",
          searchParams.status === "Paid" ? "paid" : "pending"
        );
      }
      if (selectedAction !== "All Actions")
        params.append("action", selectedAction);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      // Make API request to download Excel file
      const response = await api.get(
        `/students/export-excel?${params.toString()}`,
        {
          responseType: "blob",
        }
      );

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with date range if provided
      let filename = "students_export";
      if (startDate && endDate) {
        filename += `_${startDate}_to_${endDate}`;
      } else {
        filename += `_${new Date().toISOString().split("T")[0]}`;
      }
      link.setAttribute("download", `${filename}.xlsx`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast("success", "Excel file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      showToast("error", "Failed to export data to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [searchParams, selectedAction]); // Added searchParams to the dependency array

  return (
    <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg p-6">
      <div className="bg-blue-800 text-white text-lg font-semibold px-4 py-3 rounded-t">
        All Students
      </div>

      <div className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b">
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <button
              onClick={() => setIsProgramDropdownOpen(!isProgramDropdownOpen)}
              className="px-4 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
            >
              {searchParams.program}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isProgramDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {["All Programs", "BSCS", "BSAI", "BSCyS", "BSSE"].map(
                    (program) => (
                      <button
                        key={program}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                        onClick={() => {
                          setSearchParams((prev) => ({ ...prev, program }));
                          setIsProgramDropdownOpen(false);
                        }}
                      >
                        {program}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className="px-4 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
            >
              {searchParams.status}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {["All Status", "Paid", "Pending"].map((status) => (
                    <button
                      key={status}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      onClick={() => {
                        setSearchParams((prev) => ({ ...prev, status }));
                        setIsStatusDropdownOpen(false);
                      }}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setIsDegreeDropdownOpen(!isDegreeDropdownOpen)}
              className="px-4 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
            >
              {searchParams.degree}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isDegreeDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {["All Degrees", "BS", "MS"].map((degree) => (
                    <button
                      key={degree}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      onClick={() => {
                        setSearchParams((prev) => ({ ...prev, degree }));
                        setIsDegreeDropdownOpen(false);
                      }}
                    >
                      {degree}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setIsActionDropdownOpen(!isActionDropdownOpen)}
              className="px-4 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
            >
              {selectedAction}
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            {isActionDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                  {["All Actions", "Approve", "Approved"].map((action) => (
                    <button
                      key={action}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                      onClick={() => {
                        setSelectedAction(action);
                        setIsActionDropdownOpen(false);
                      }}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-2 w-full md:w-auto">
          <div className="relative flex items-center w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by email"
              className="pl-10 pr-4 py-2 border rounded-l-md w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchParams.email}
              onChange={(e) =>
                setSearchParams((prev) => ({ ...prev, email: e.target.value }))
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <svg
              className="w-5 h-5 text-gray-400 absolute left-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Search
            </button>
          </div>
        </div>
      </div>
      {/*  download student  */}
      {/* <div className="flex items-center gap-2 mt-2 md:mt-0">
        <button
          onClick={() => setShowDateFilter(!showDateFilter)}
          className="px-3 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Date Range
        </button>

        <button
          onClick={handleExportToExcel}
          disabled={isExporting}
          className={`px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center ${
            isExporting ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isExporting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Download Students
            </>
          )}
        </button>
      </div> */}

      <div className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b">
        <div className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b">
          <button
            onClick={() => setShowDateFilter(!showDateFilter)}
            className="px-4 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center w-full sm:w-auto"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Date Range
          </button>
          <p className="px-4 py-2 text-gray-700 bg-white border rounded-md  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center w-full sm:w-auto">
            Total Applications = {filteredStudents && filteredStudents.length}
          </p>
        </div>

        <button
          onClick={handleExportToExcel}
          disabled={isExporting}
          className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center w-full sm:w-auto ${
            isExporting ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isExporting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-5 h-5 mr-2" />
              Download Students Records
            </>
          )}
        </button>
      </div>

      {showDateFilter && (
        <div className="p-4 border-b flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <label htmlFor="startDate" className="mr-2 font-medium">
              From:
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center">
            <label htmlFor="endDate" className="mr-2 font-medium">
              To:
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => {
              setStartDate("");
              setEndDate("");
            }}
            className="px-3 py-2 text-gray-700 bg-white border rounded-md hover:bg-gray-50 focus:outline-none"
          >
            Clear
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="py-4 px-6">NAME</th>
              <th className="py-4 px-6">Level</th>
              <th className="py-4 px-6">Applied Program</th>
              <th className="py-4 px-6">STATUS</th>
              <th className="py-4 px-6">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student) => (
              <tr key={student.id} className="border-b hover:bg-gray-50">
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${student.image}`}
                        alt={student.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium">{student.name}</span>
                      <span className="text-sm text-gray-500">
                        {student.email}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">{student.level}</td>
                <td className="py-4 px-6">{student.program}</td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        student.fee_voucher != null
                          ? "bg-green-500"
                          : "bg-red-500"
                      }`}
                    />
                    <span className="capitalize">
                      {student.fee_voucher != null ? "Fee Paid" : "Pending"}
                    </span>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex gap-2">
                    <button
                      className={`px-4 py-1 text-sm border rounded flex items-center justify-center ${
                        viewModalLoading === student.email
                          ? "text-blue-400 border-blue-400 bg-blue-50 cursor-wait"
                          : "text-blue-600 border-blue-600 hover:bg-blue-50"
                      }`}
                      onClick={() => handleViewClick(student.email)}
                      disabled={viewModalLoading === student.email}
                    >
                      {viewModalLoading === student.email ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading...
                        </>
                      ) : (
                        "View"
                      )}
                    </button>
                    {/* <button
                      onClick={() => handleEditClick(student.email)}
                      className="px-4 py-1 text-sm border rounded flex items-center justify-center text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                    >
                      Edit
                    </button> */}
                    <button
                      onClick={() => handleDeleteClick(student.email)}
                      className="px-4 py-1 text-sm border rounded flex items-center justify-center text-red-600 border-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => handleApprove(student.email)}
                      className={`px-4 py-1 text-sm border rounded flex items-center justify-center ${
                        loadingApprove[student.email]
                          ? "text-green-400 border-green-400 bg-green-50 cursor-wait"
                          : student.admit_card == null
                          ? "text-green-600 border-green-600 hover:bg-green-50"
                          : "bg-green-200 text-green-600 border-green-600 cursor-not-allowed"
                      }`}
                      disabled={
                        student.admit_card == "1" ||
                        loadingApprove[student.email]
                      }
                    >
                      {loadingApprove[student.email] ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-green-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Approving...
                        </>
                      ) : student.admit_card == "1" ? (
                        "Approved"
                      ) : (
                        "Approve"
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
            <p className="mb-6">
              Are you sure you want to delete this student record? This will
              remove all data including academic records and cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                disabled={loadingDelete}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                disabled={loadingDelete}
              >
                {loadingDelete ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllStudents;
